import { CalendarDaysIcon, MapPinIcon, RepeatIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';

import { useCalendarFormat } from '../lib/format';

/** Search page rendering of `event` documents (content of the page's link row): title, when (in the reader's zone), place, recurring mark. */
export function EventSearchResult({
  result,
  highlights,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
}) {
  const { t } = useTranslation('calendar');
  const fmt = useCalendarFormat();
  const meta = result.meta ?? {};
  const allDay = meta.isAllDay === 'true';
  const snippet = highlights?.snippet ?? result.snippet;
  return (
    <>
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background text-brand-text shadow-xs">
        <CalendarDaysIcon className="size-[1.125rem]" strokeWidth={1.8} aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1" data-testid="event-search-result">
        <span className="truncate text-[0.9375rem] font-medium text-foreground">
          {highlights?.title ?? result.title}
        </span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {meta.start && meta.end && (
            <span className="inline-flex items-center gap-1">
              <CalendarDaysIcon className="size-3.5" aria-hidden />
              {fmt.range(meta.start, meta.end, allDay)}
            </span>
          )}
          {meta.isRecurring === 'true' && (
            <span className="inline-flex items-center gap-1">
              <RepeatIcon className="size-3.5" aria-hidden />
              {t('event.recurring')}
            </span>
          )}
          {meta.location && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPinIcon className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">{meta.location}</span>
            </span>
          )}
          {meta.calendarKind === 'company' && <span>{t('kinds.company')}</span>}
        </span>
        {snippet && <span className="line-clamp-2 text-sm text-muted-foreground">{snippet}</span>}
      </span>
    </>
  );
}
